/*
 * File: /apps/web/src/user/redux/index.js
 * Project: tr-web
 * Created Date: 2020-02-25
 * Author: Raif
 *
 * Copyright (c) 2020 TaskRabbit, Inc
 */

import xhr from "util/xhr";
import {
  USER_LOGGED_IN,
  USER_LOCAL_LOGIN,
  VERIFY_LOGIN_TWO_FACTOR_AUTH,
  UPDATE_TWO_FACTOR_MODAL_STEP,
  RESEND_TWO_FACTOR_LOGIN_CODE,
  SEND_TWO_FACTOR_LOGIN_CODE,
  SETUP_TWO_FACTOR_AUTH,
  VERIFY_SETUP_LOGIN_TWO_FACTOR_AUTH,
} from "login/redux/modules/loginSignup";
import {
  LOGIN,
  RESEND_TWO_FACTOR_AUTH_CODE,
  SEND_TWO_FACTOR_CODE,
  ACTIVATE_TWO_FACTOR_AUTH,
  LOGOUT,
} from "login/routes/apiRoutes";
// eslint-disable-next-line camelcase
import { snake_caseKeys } from "util/casing";
import {
  twoFactorModalSteps,
  SESSION_SIGN_IN,
} from "enums/twoFactorAuth";
import { BOOTSTRAP, CURRENT_USER } from "../../routes/apiRoutes";
import { isLoggedIn } from "../../../shared/utils/userService";


const initialState = {
  abDecision: "",
  abDecisionId: "",
  activeTermsVersionUrl: "",
  avatarUrl: "",
  email: "",
  firstName: "",
  isAdmin: false,
  isTasker: false,
  lastName: "",
  loading: false,
  locale: "",
  loggedIn: isLoggedIn(),
  metroId: undefined,
  metroName: undefined,
  postalCode: "",
  twoFactorAuth: {},
  twoFactorAuthLoading: false,
  userId: "",
};

export const CURRENT_USER_REQUEST = "v3/user/CURRENT_USER_REQUEST";
export const CURRENT_USER_SUCCESS = "v3/user/CURRENT_USER_SUCCESS";
export const CURRENT_USER_FAILURE = "v3/user/CURRENT_USER_FAILURE";
export const CURRENT_USER_LOGOUT = "v3/user/CURRENT_USER_LOGOUT";
export const UPDATE_USER_CONSENT_REQUEST =
  "v3/user/UPDATE_USER_CONSENT_REQUEST";
export const UPDATE_USER_CONSENT_SUCCESS =
  "v3/user/UPDATE_USER_CONSENT_SUCCESS";
export const UPDATE_USER_CONSENT_FAILURE =
  "v3/user/UPDATE_USER_CONSENT_FAILURE";

// eslint-disable-next-line @typescript-eslint/default-param-last
export default function user(state = initialState, action) {
  switch (action.type) {
    case USER_LOCAL_LOGIN:
      return {
        ...state,
        marketingConsentEnabled: !action.values.marketing_opted_out,
      };
    case CURRENT_USER_REQUEST:
      return { ...state, loading: true };
    case CURRENT_USER_SUCCESS:
      return {
        ...state,
        loading: action.loading,
        abNames: action.abNames,
        abDecision: action.abDecision,
        abDecisionId: action.abDecisionId,
        capabilities: action.capabilities,
        userId: action.userId,
        firstName: action.firstName,
        lastName: action.lastName,
        email: action.email,
        loggedIn: action.loggedIn,
        locale: action.locale,
        avatarUrl: action.avatarUrl,
        isAdmin: action.isAdmin,
        isTasker: action.isTasker,
        metroId: action.metroId,
        metroName: action.metroName,
        postalCode: action.postalCode,
        marketingConsentEnabled: action.marketingConsentEnabled,
        activeTermsVersionUrl: action.activeTermsVersionUrl,
        updateTermsConsentKey: action.updateTermsConsentKey,
      };
    case CURRENT_USER_FAILURE:
      return state;
    case UPDATE_USER_CONSENT_SUCCESS: {
      return {
        ...state,
        marketingConsentEnabled: action.marketingConsentEnabled,
        termsConsentKey: action.updateTermsConsentKey,
      };
    }
    case USER_LOGGED_IN: {
      const { data } = action;
      return data
        ? {
          ...state,
          userId: data.id,
          firstName: data.first_name,
          lastName: data.last_name,
          email: data.email,
          loggedIn: true,
          locale: data.locale,
          twoFactorAuth: {
            id: data.mfa_id,
            next: data.next,
            phone: data.mfa_phone,
          },
        }
        : state;
    }
    case SEND_TWO_FACTOR_LOGIN_CODE.REQUEST:
    case VERIFY_LOGIN_TWO_FACTOR_AUTH.REQUEST:
    case VERIFY_SETUP_LOGIN_TWO_FACTOR_AUTH.REQUEST: {
      return {
        ...state,
        twoFactorAuth: {
          ...state.twoFactorAuth,
          loading: true,
        },
      };
    }
    case VERIFY_LOGIN_TWO_FACTOR_AUTH.SUCCESS: {
      return {
        ...state,
        twoFactorAuth: {
          ...action.data,
          loading: false,
          error: null,
        },
      };
    }
    case VERIFY_LOGIN_TWO_FACTOR_AUTH.FAILURE: {
      return {
        ...state,
        twoFactorAuth: {
          ...state.twoFactorAuth,
          error: action.data,
          loading: false,
        },
      };
    }
    case SEND_TWO_FACTOR_LOGIN_CODE.SUCCESS: {
      return {
        ...state,
        twoFactorAuth: {
          ...state.twoFactorAuth,
          mfaPhone: action.data,
          loading: false,
          next: twoFactorModalSteps.verify,
          setup: true,
          error: null,
        },
      };
    }
    case UPDATE_TWO_FACTOR_MODAL_STEP: {
      return {
        ...state,
        twoFactorAuth: {
          ...state.twoFactorAuth,
          next: action.data,
          error: null,
        },
      };
    }
    case RESEND_TWO_FACTOR_LOGIN_CODE.SUCCESS: {
      return {
        ...state,
        twoFactorAuth: {
          ...state.twoFactorAuth,
          codeResent: action.data,
          loading: false,
          error: null,
        },
      };
    }
    case RESEND_TWO_FACTOR_LOGIN_CODE.FAILURE: {
      return {
        ...state,
        twoFactorAuth: {
          ...state.twoFactorAuth,
          error: action.data,
          loading: false,
        },
      };
    }
    case SEND_TWO_FACTOR_LOGIN_CODE.FAILURE:
      return {
        ...state,
        twoFactorAuth: {
          ...state.twoFactorAuth,
          error: action.data,
          loading: false,
        },
      };
    case VERIFY_SETUP_LOGIN_TWO_FACTOR_AUTH.SUCCESS:
      return {
        ...state,
        twoFactorAuth: {
          ...state.twoFactorAuth,
          next: twoFactorModalSteps.success,
          loading: false,
          error: null,
        },
      };
    case VERIFY_SETUP_LOGIN_TWO_FACTOR_AUTH.FAILURE:
      return {
        ...state,
        twoFactorAuth: {
          ...state.twoFactorAuth,
          error: action.data,
          loading: false,
        },
      };
    case SETUP_TWO_FACTOR_AUTH: {
      return {
        ...state,
        loggedInPlaceholder: action.data,
        twoFactorAuth: {
          ...state.twoFactorAuth,
          next: twoFactorModalSteps.setup,
          loading: false,
          setup: true,
        },
      };
    }
    case CURRENT_USER_LOGOUT: {
      return {
        ...state,
        loggedIn: false,
      };
    }
    default:
      return state;
  }
}

export const initalizeCurrentUser = () => {
  return async (dispatch) => {
    dispatch({
      type: CURRENT_USER_REQUEST,
    });
    try {
      const { data } = await xhr.get(BOOTSTRAP);
      const loggedInUser = (await xhr.get(CURRENT_USER)).data;
      const abNames = data.ab_decision.split(":").filter(Boolean);

      dispatch({
        type: CURRENT_USER_SUCCESS,
        loading: false,
        abNames,
        abDecision: data.ab_decision,
        abDecisionId: data.ab_decision_id,
        capabilities: loggedInUser?.capabilities,
        userId: loggedInUser?.id,
        firstName: loggedInUser.first_name,
        lastName: loggedInUser.last_name,
        email: loggedInUser.email,
        loggedIn: !!loggedInUser?.id,
        locale: loggedInUser.locale || window.LOCALE,
        avatarUrl: loggedInUser.avatar_url,
        isAdmin: loggedInUser.admin,
        isTasker: loggedInUser.rabbit,
        metroId: loggedInUser.metro_id,
        metroName: loggedInUser.metro_name,
        postalCode: loggedInUser.postal_code,
        marketingConsentEnabled: data.marketing_consent_enabled,
        activeTermsVersionUrl: data.active_terms_version_url,
        updateTermsConsentKey: data.update_terms_consent_key,
      });
    } catch (ex) {
      dispatch({ type: CURRENT_USER_FAILURE, data: ex });
    }
  };
};

export const updateUserConsentInfo = (consentData) => {
  return async (dispatch) => {
    dispatch({
      type: UPDATE_USER_CONSENT_REQUEST,
    });
    try {
      const { camelCasedData: data } = await xhr.put(
        "/api/v3/account.json",
        snake_caseKeys(consentData)
      );

      dispatch({
        type: UPDATE_USER_CONSENT_SUCCESS,
        marketingConsentEnabled: data.marketingConsentEnabled,
        termsConsentKey: data.updateTermsConsentKey,
      });
    } catch (error) {
      dispatch({ type: UPDATE_USER_CONSENT_FAILURE, data: error });
    }
  };
};

export const updateTwoFactorAuthModalStep = (data) => ({
  type: UPDATE_TWO_FACTOR_MODAL_STEP,
  data,
});

export const resendSetupLoginCode = () => async (dispatch) => {
  try {
    await xhr.put(SEND_TWO_FACTOR_CODE, { action_name: SESSION_SIGN_IN });

    dispatch({
      type: RESEND_TWO_FACTOR_LOGIN_CODE.SUCCESS,
      data: true,
    });
    setTimeout(() => {
      dispatch({
        type: RESEND_TWO_FACTOR_LOGIN_CODE.SUCCESS,
        data: false,
      });
    }, 60000);
  } catch (err) {
    dispatch({
      type: RESEND_TWO_FACTOR_LOGIN_CODE.FAILURE,
      data: err.response.data.message,
    });
  }
};

export const resendTwoFactorLoginCode = () => async (dispatch, getState) => {
  try {
    const {
      twoFactorAuth: { mfaId },
    } = getState().user;

    await xhr.put(RESEND_TWO_FACTOR_AUTH_CODE, { mfa_id: mfaId, action_name: SESSION_SIGN_IN });

    dispatch({
      type: RESEND_TWO_FACTOR_LOGIN_CODE.SUCCESS,
      data: true,
    });
    setTimeout(() => {
      dispatch({
        type: RESEND_TWO_FACTOR_LOGIN_CODE.SUCCESS,
        data: false,
      });
    }, 60000);
  } catch (err) {
    dispatch({
      type: RESEND_TWO_FACTOR_LOGIN_CODE.FAILURE,
      data: err.response.data.message,
    });
  }
};

// Verify step when signing up
export const verifySetupLoginCode = (code) => async (dispatch) => {
  dispatch({
    type: VERIFY_SETUP_LOGIN_TWO_FACTOR_AUTH.REQUEST,
  });

  try {
    await xhr.post(ACTIVATE_TWO_FACTOR_AUTH, { code, action_name: SESSION_SIGN_IN });
    dispatch({
      type: VERIFY_SETUP_LOGIN_TWO_FACTOR_AUTH.SUCCESS,
    });
  } catch (err) {
    dispatch({
      type: VERIFY_SETUP_LOGIN_TWO_FACTOR_AUTH.FAILURE,
      data: err.response.data.message,
    });
  }
};

// Verify step when already signed up for two-factor
export const verifyTwoFactorLogin = (code) => async (dispatch, getState) => {
  dispatch({
    type: VERIFY_LOGIN_TWO_FACTOR_AUTH.REQUEST,
  });

  try {
    const {
      twoFactorAuth: { mfaId },
    } = getState().user;

    const response = await xhr.post(LOGIN, {
      code,
      mfa_id: mfaId,
    });

    dispatch({
      type: USER_LOGGED_IN,
      data: {
        ...response.camelCasedData,
        twoFactorAuth: null,
      },
    });
  } catch (err) {
    dispatch({
      type: VERIFY_LOGIN_TWO_FACTOR_AUTH.FAILURE,
      data: err.response.data.message,
    });
  }
};

export const sendTwoFactorCode = (phone) => async (dispatch) => {
  try {
    await xhr.post(SEND_TWO_FACTOR_CODE, { phone, action_name: SESSION_SIGN_IN });

    dispatch({
      type: SEND_TWO_FACTOR_LOGIN_CODE.SUCCESS,
      data: phone,
    });
  } catch (err) {
    dispatch({
      type: SEND_TWO_FACTOR_LOGIN_CODE.FAILURE,
      data: err.response.data.message,
    });
  }
};

export const loginFromTwoFactorSetup = () => (dispatch, getState) => {
  const { loggedInPlaceholder } = getState().user;

  dispatch({
    type: USER_LOGGED_IN,
    data: loggedInPlaceholder,
  });
};

export const logout = () => async (dispatch) => {
  await xhr.get(LOGOUT);

  dispatch({
    type: CURRENT_USER_LOGOUT,
  });
};
